import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  Link,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { Package, MapPin, Storefront } from '@phosphor-icons/react';
import { resolveProductImageUrl, resolveProductImageUrlFromFields, buildProductDetailPath, toSqlDateKey, productRowDateKey, productTitleCountryKey, PRODUCT_FLAG_LABEL_FOR_NEW_USERS_ONLY, PRODUCT_FLAG_LABEL_SKU_VISIBILITY, getSkuVisibility, getSkuVisibilityLabel } from './productUtils';
import { extractVariantsFromRaw } from './matchingUtils';
import SkuTitleWithTranslate from './components/SkuTitleWithTranslate';
import dayjs from 'dayjs';

const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/300x200?text=Product+Image';

const normalizeCurrencyCode = (value) => (value || '').toString().trim().toUpperCase();

const formatPriceWithLocal = (displayCurrency, displayValue, localPricing, localKey) => {
  const main = `${displayCurrency || ''} ${displayValue}`.trim();
  const localValue = localPricing?.[localKey];
  const localCurrency = localPricing?.currency;
  if (
    localValue == null ||
    localValue === '' ||
    !localCurrency ||
    normalizeCurrencyCode(displayCurrency) === normalizeCurrencyCode(localCurrency)
  ) {
    return main;
  }
  return `${main} (${localCurrency} ${localValue})`;
};

const DEVICE_CATEGORY_GROUPS = ['Devices', 'Device Bundles', 'Starter Kits', 'Accessories'];
const STICKS_CATEGORY_GROUPS = ['Sticks', 'Sticks Bundles'];

/** Default matches JTI classic accent when parent does not pass a theme accent. */
const DEFAULT_ACCENT_COLOR = '#2e7d32';

/**
 * Popup showing product image and key details. Used when clicking chart line labels/images.
 * For devices: Colours, Pricing & availability.
 * For sticks: Flavours, Pricing & availability, Number of units.
 */
function ProductDetailPopup({
  open,
  onClose,
  productName,
  productImage,
  productData,
  productDataRows = [],
  groupProductNames,
  packSizeOverrides = {},
  productFlags = {},
  pricingAsOfDate,
  pinnedChartPrices = false,
  activePriceTypes,
  projectKey,
  country: countryProp,
  accentColor = DEFAULT_ACCENT_COLOR,
  getDisplayTitle,
  isTranslationActive,
  isTranslatingTitle,
  onToggleTranslate,
}) {
  const p = productData || {};
  const rows = productDataRows?.length ? productDataRows : (productData ? [productData] : []);
  const hasGroupList = Array.isArray(groupProductNames) && groupProductNames.length > 1;
  const popupCountry = countryProp || p?.country || rows.find((row) => row?.country)?.country || '';
  const rowsByTitle = React.useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const t = row?.title;
      if (!t) return;
      const keyed = productTitleCountryKey(t, row.country);
      if (!map.has(keyed)) map.set(keyed, row);
      if (!map.has(t)) map.set(t, row);
    });
    return map;
  }, [rows]);
  const lookupRowByTitle = (title) => {
    if (!title) return null;
    if (popupCountry) {
      const keyed = rowsByTitle.get(productTitleCountryKey(title, popupCountry));
      if (keyed) return keyed;
    }
    return rowsByTitle.get(title) || null;
  };
  const [selectedGroupTitle, setSelectedGroupTitle] = React.useState(null);
  React.useEffect(() => {
    if (!hasGroupList) {
      setSelectedGroupTitle(null);
      return;
    }
    const first = groupProductNames?.[0] || productName || null;
    setSelectedGroupTitle(first);
  }, [hasGroupList, groupProductNames, productName, open]);
  const effectiveTitle = hasGroupList ? (selectedGroupTitle || groupProductNames?.[0] || productName) : productName;
  const detailProduct =
    (pinnedChartPrices && p && (!hasGroupList || !selectedGroupTitle || selectedGroupTitle === (p.title || productName))
      ? p
      : null) ||
    (effectiveTitle && lookupRowByTitle(effectiveTitle)) ||
    (hasGroupList && rows.length ? rows[0] : null) ||
    p;
  const detailRows = hasGroupList ? (detailProduct ? [detailProduct] : []) : rows;

  // Resolve image: prefer productImage, else primary row, else any row. Each image field
  // (image, box_image_urls, image_urls, media_gallery_urls) is tried in priority order so an
  // unresolvable earlier field (common for GLO) never hides a valid later one.
  const resolveRowImage = (r) =>
    resolveProductImageUrlFromFields(r?.image, r?.box_image_urls, r?.image_urls, r?.media_gallery_urls);
  const imageSrc = resolveProductImageUrl(productImage)
    || resolveRowImage(detailProduct)
    || rows.reduce((acc, r) => acc || resolveRowImage(r), null)
    || PLACEHOLDER_IMAGE;
  const categoryGroup = detailProduct?.category_group || 'N/A';
  const resolvedAsOfDateKey = toSqlDateKey(pricingAsOfDate) || productRowDateKey(detailProduct) || null;
  const resolvedAsOfDateLabel = (() => {
    if (!resolvedAsOfDateKey) return null;
    const parsed = dayjs(resolvedAsOfDateKey);
    return parsed.isValid() ? parsed.format('D. M. YYYY') : String(resolvedAsOfDateKey);
  })();

  const isDevice = DEVICE_CATEGORY_GROUPS.includes(categoryGroup);
  const isSticks = STICKS_CATEGORY_GROUPS.includes(categoryGroup);
  const flags = productFlags[effectiveTitle] || {};
  const isStarterKitOrRegistration = !!flags.isStarterKitOrRegistration;
  const isLimitedEdition = flags.portfolioType === 'Limited edition';
  const isPromoOffer = !!flags.isPromoOffer;
  const isForNewUsersOnly = !!flags.isForNewUsersOnly;
  const skuVisibilityLabel = getSkuVisibilityLabel(getSkuVisibility(productFlags, effectiveTitle));

  // Aggregate variants (colours/flavours) from all rows
  const variants = React.useMemo(() => {
    const seen = new Set();
    detailRows.forEach((row) => {
      [row.variant, row.product_variants, row.product_variant_names].forEach((raw) => {
        extractVariantsFromRaw(raw).forEach((v) => seen.add(v));
      });
    });
    return [...seen].sort();
  }, [detailRows]);

  // Effective pack size: override first, then from data
  const effectiveUnits = React.useMemo(() => {
    const saved = packSizeOverrides[effectiveTitle];
    if (saved) {
      if (saved.packSize === 'other') {
        const n = Number(saved.customPackSize);
        return !isNaN(n) && n > 0 ? n : null;
      }
      const n = Number(saved.packSize);
      return !isNaN(n) && n > 0 ? n : null;
    }
    const raw = detailProduct?.['Units (#)'];
    const n = raw !== undefined && raw !== null && raw !== '' ? Number(raw) : NaN;
    return !isNaN(n) && n > 0 ? n : null;
  }, [packSizeOverrides, effectiveTitle, detailProduct]);
  const selectedPriceTypes = activePriceTypes instanceof Set
    ? activePriceTypes
    : new Set(Array.isArray(activePriceTypes) ? activePriceTypes : ['actual', 'regular', 'promo', 'starter_kit']);

  const formatVariantPriceSummary = (row) => {
    if (!row) return 'No pricing data';
    const currency = row.currency || '';
    const localPricing = row.localCurrencyPricing;
    const actual = selectedPriceTypes.has('actual') && row.actual_price != null && row.actual_price !== ''
      ? formatPriceWithLocal(currency, row.actual_price, localPricing, 'actual_price')
      : null;
    const regular = selectedPriceTypes.has('regular') && row.regular_price != null && row.regular_price !== ''
      ? formatPriceWithLocal(currency, row.regular_price, localPricing, 'regular_price')
      : null;
    const promoRaw = row.promo_price ?? row.promoPrice ?? row.__avgPromoRaw;
    const promoNum = promoRaw != null && promoRaw !== '' && !Number.isNaN(Number(promoRaw)) ? Number(promoRaw) : null;
    const regularNum = row.regular_price != null && row.regular_price !== '' && !Number.isNaN(Number(row.regular_price))
      ? Number(row.regular_price)
      : null;
    const promo =
      selectedPriceTypes.has('promo') &&
      promoNum != null &&
      regularNum != null &&
      promoNum !== regularNum
        ? formatPriceWithLocal(currency, promoNum, localPricing, 'actual_price')
        : null;
    const pieces = [];
    if (actual) pieces.push(`Actual: ${actual}`);
    if (regular) pieces.push(`Listed: ${regular}`);
    if (promo) pieces.push(`Promo: ${promo}`);
    if (pieces.length) return pieces.join(' | ');
    return 'No pricing data';
  };
  const resolveUnitsForTitle = (title, row) => {
    const saved = packSizeOverrides[title];
    if (saved) {
      if (saved.packSize === 'other') {
        const n = Number(saved.customPackSize);
        if (!Number.isNaN(n) && n > 0) return n;
      } else {
        const n = Number(saved.packSize);
        if (!Number.isNaN(n) && n > 0) return n;
      }
    }
    const raw = row?.['Units (#)'];
    const n = raw !== undefined && raw !== null && raw !== '' ? Number(raw) : NaN;
    return !Number.isNaN(n) && n > 0 ? n : null;
  };
  const isRowOutOfStock = (row) => {
    const raw = String(row?.stock_status || row?.availability || '').trim().toLowerCase();
    if (!raw) return false;
    return /out\s*of\s*stock|sold\s*out|unavailable|not\s*in\s*stock|\boos\b/.test(raw);
  };
  const formatGroupVariantName = (name) => {
    const row = lookupRowByTitle(name);
    const rowCategory = row?.category_group || '';
    const rowIsSticks = STICKS_CATEGORY_GROUPS.includes(rowCategory) || isSticks;
    const oosSuffix = isRowOutOfStock(row) ? ' (Out of stock)' : '';
    if (!rowIsSticks) return `${name}${oosSuffix}`;
    const units = resolveUnitsForTitle(name, row);
    const withUnits = units != null && units > 1 ? `${name} (${units} units)` : name;
    return `${withUnits}${oosSuffix}`;
  };

  const localPricing = detailProduct?.localCurrencyPricing;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 600, color: accentColor }}>
          {hasGroupList ? 'Products on this line' : 'Product Details'}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ color: accentColor }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          maxHeight: hasGroupList ? 'min(70vh, 560px)' : undefined,
          overflowY: hasGroupList ? 'auto' : undefined,
        }}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          {hasGroupList && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: accentColor }}>
                All variants ({groupProductNames.length})
              </Typography>
              <Box
                sx={{
                  maxHeight: 220,
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 0.5,
                }}
              >
                {groupProductNames.map((name) => (
                  <Box
                    key={name}
                    component="button"
                    type="button"
                    onClick={() => setSelectedGroupTitle(name)}
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      background: 'transparent',
                      py: 0.75,
                      px: 1,
                      fontSize: '0.875rem',
                      color: accentColor,
                      cursor: 'pointer',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                      '&:hover': { bgcolor: 'action.hover' },
                      ...(effectiveTitle === name ? { bgcolor: 'action.selected' } : {}),
                    }}
                  >
                    <Typography variant="body2" sx={{ color: accentColor, fontWeight: effectiveTitle === name ? 700 : 500 }}>
                      {formatGroupVariantName(name)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatVariantPriceSummary(lookupRowByTitle(name))}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {/* Product Image - fixed-height container to prevent wobble when image is missing */}
          <Box
            sx={{
              width: '100%',
              minHeight: 150,
              maxHeight: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              border: '1px solid #e0e0e0',
              bgcolor: '#ffffff',
              overflow: 'hidden',
            }}
          >
            {imageSrc && imageSrc !== PLACEHOLDER_IMAGE ? (
              <Box
                component="img"
                src={imageSrc}
                alt={effectiveTitle || 'Product'}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
                sx={{
                  width: '100%',
                  maxHeight: 200,
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 150,
                  bgcolor: '#ffffff',
                }}
              />
            )}
          </Box>

          {/* Title */}
          <Typography variant="h6" sx={{ fontWeight: 600, color: accentColor }}>
            {onToggleTranslate && getDisplayTitle ? (
              <SkuTitleWithTranslate
                title={effectiveTitle || detailProduct?.title || 'Unknown product'}
                country={detailProduct?.country}
                getDisplayTitle={getDisplayTitle}
                isTranslationActive={isTranslationActive}
                isTranslatingTitle={isTranslatingTitle}
                onToggleTranslate={onToggleTranslate}
              />
            ) : (
              effectiveTitle || detailProduct?.title || 'Unknown product'
            )}
          </Typography>

          {/* Price-type / promo flags — Audience visibility is a separate listing attribute */}
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="body2">
              <strong>Starter Kit / Registration Price:</strong> {isStarterKitOrRegistration ? 'Yes' : 'No'}
            </Typography>
            <Typography variant="body2">
              <strong>Limited edition:</strong> {isLimitedEdition ? 'Yes' : 'No'}
            </Typography>
            <Typography variant="body2">
              <strong>Promo offer:</strong> {isPromoOffer ? 'Yes' : 'No'}
            </Typography>
            <Typography variant="body2">
              <strong>{PRODUCT_FLAG_LABEL_FOR_NEW_USERS_ONLY}:</strong> {isForNewUsersOnly ? 'Yes' : 'No'}
            </Typography>
          </Box>
          <Typography variant="body2">
            <strong>{PRODUCT_FLAG_LABEL_SKU_VISIBILITY}:</strong> {skuVisibilityLabel}
          </Typography>

          {/* Chips */}
          <Box display="flex" gap={1} flexWrap="wrap">
            {detailProduct?.brand && (
              <Chip
                icon={<Package size={14} />}
                label={detailProduct.brand}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: accentColor,
                  color: accentColor,
                  '& .MuiChip-icon': { color: accentColor },
                }}
              />
            )}
            {detailProduct?.country && (
              <Chip icon={<MapPin size={14} />} label={detailProduct.country} color="secondary" variant="outlined" size="small" />
            )}
            {detailProduct?.retailer && (
              <Chip icon={<Storefront size={14} />} label={detailProduct.retailer} color="success" variant="outlined" size="small" />
            )}
          </Box>

          {/* Device: Colours */}
          {isDevice && variants.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: accentColor }}>
                Colours
              </Typography>
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {variants.map((v) => (
                  <Chip key={v} label={v} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}

          {/* Sticks: Flavours */}
          {isSticks && variants.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: accentColor }}>
                Flavours
              </Typography>
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {variants.map((v) => (
                  <Chip key={v} label={v} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}

          {/* Pricing & availability (both device and sticks) */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: accentColor }}>
              Pricing & availability
            </Typography>
            {resolvedAsOfDateLabel ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {`Prices as of ${resolvedAsOfDateLabel}.`}
              </Typography>
            ) : null}
            <Box display="flex" flexDirection="column" gap={0.5}>
              {selectedPriceTypes.has('actual') && detailProduct?.actual_price != null && (
                <Typography variant="body2">
                  <strong>Actual price:</strong>{' '}
                  {formatPriceWithLocal(detailProduct.currency, detailProduct.actual_price, localPricing, 'actual_price')}
                </Typography>
              )}
              {selectedPriceTypes.has('regular') && detailProduct?.regular_price != null && (
                <Typography variant="body2">
                  <strong>Regular price:</strong>{' '}
                  {formatPriceWithLocal(detailProduct.currency, detailProduct.regular_price, localPricing, 'regular_price')}
                </Typography>
              )}
              {selectedPriceTypes.has('promo') &&
                detailProduct?.promo_price != null &&
                detailProduct?.regular_price != null &&
                Number(detailProduct.promo_price) !== Number(detailProduct.regular_price) && (
                <Typography variant="body2">
                  <strong>Promo price:</strong>{' '}
                  {formatPriceWithLocal(detailProduct.currency, detailProduct.promo_price, localPricing, 'actual_price')}
                </Typography>
              )}
              {detailProduct?.stock_status != null && (
                <Typography variant="body2">
                  <strong>Stock:</strong> {detailProduct.stock_status}
                </Typography>
              )}
              {(
                !(
                  (selectedPriceTypes.has('actual') && detailProduct?.actual_price) ||
                  (selectedPriceTypes.has('regular') && detailProduct?.regular_price) ||
                  (selectedPriceTypes.has('promo') &&
                    detailProduct?.promo_price != null &&
                    detailProduct?.regular_price != null &&
                    Number(detailProduct.promo_price) !== Number(detailProduct.regular_price)) ||
                  detailProduct?.stock_status
                )
              ) && (
                <Typography variant="body2" color="text.secondary">
                  No pricing data available
                </Typography>
              )}
            </Box>
          </Box>

          {/* Sticks only: Number of units */}
          {isSticks && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: accentColor }}>
                Number of units
              </Typography>
              <Typography variant="body2">
                {effectiveUnits != null ? effectiveUnits : 'N/A'}
              </Typography>
            </Box>
          )}

          {/* Link to full product detail */}
          {projectKey && effectiveTitle && (
            <Link
              component={RouterLink}
              to={buildProductDetailPath(
                projectKey,
                popupCountry || detailProduct?.country,
                effectiveTitle,
                resolvedAsOfDateKey
              )}
              onClick={onClose}
              sx={{ mt: 1, fontWeight: 600, color: accentColor, '&:hover': { color: accentColor, opacity: 0.85 } }}
            >
              View full product details →
            </Link>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default ProductDetailPopup;
